import { alertFullScreen } from '../../alertDispatcher';
import { dispatchMissionUpdated } from '../../missionEvents';
import { dispatchSamplesUpdated } from '../../mapEvents';

interface ResampleWaypoint {
  waypoint_number: number;
  getCorePoint(): { getSoilCore(): { getSample(): ResampleSample | undefined } | undefined } | undefined;
}

interface ResampleSample {
  sample_id: string;
  getPulledSoilCores(): unknown[] | undefined;
  resetPulled(): void;
}

interface ResampleMission {
  getWaypoints(): ResampleWaypoint[];
}

/**
 * Checks if the target waypoint's sample has pulled cores and prompts the user.
 * Returns true if navigation should continue, false if cancelled.
 * Caller owns doneCallback — fire it on the `false` return.
 */
export async function handleResampleCheck(
  mission: ResampleMission | undefined,
  waypointNumber: number,
): Promise<boolean> {
  if (!mission) {
    return true;
  }

  const missionWaypoint = mission.getWaypoints().find((waypoint) => waypoint.waypoint_number === waypointNumber);

  if (!missionWaypoint) {
    return true;
  }

  const missionSample = missionWaypoint.getCorePoint()?.getSoilCore()?.getSample();

  if (!missionSample) {
    return true;
  }

  const pulledCores = missionSample.getPulledSoilCores() || [];

  if (pulledCores.length === 0) {
    return true;
  }

  const response = await alertFullScreen(
    'Resample?',
    `Sample ${missionSample.sample_id} has ${pulledCores.length} cores pulled, what do you want to do?`,
    ['Resample', 'Sample Additional Cores', 'Cancel Setting Waypoint'],
  );

  if (response === 'Cancel Setting Waypoint') {
    return false;
  } else if (response === 'Resample') {
    missionSample.resetPulled();
    dispatchMissionUpdated();
    // Only one specific sample's cores were reset → targeted rebuild.
    dispatchSamplesUpdated([missionSample.sample_id]);
  }
  // 'Sample Additional Cores' → continue

  return true;
}
